import { useState } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import Header from '../components/Header';

// Contact information
const companyName = "Partners Plumbing Co.";
const phoneNumber = "(630) 715-8829";
const email = "info@partnersinplumbing.com";
const address = "Kane County, IL 60177";

// WordPress URL for form submission
const wpUrl = process.env.NEXT_PUBLIC_WORDPRESS_URL?.replace(/\/$/, '') || 'https://blog.partnersinplumbing.com';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const formAction = form.getAttribute('action') || `${wpUrl}/wp-admin/admin-ajax.php`;

    try {
      // Submit to WordPress without CORS restrictions
      const response = await fetch(formAction, {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // WordPress requires this for cross-origin
      });
      
      // Since we can't read the response with no-cors, let's also try to submit 
      // via webhook to ensure we capture the data
      try {
        const webhookData = {
          "1": formData.get('wpforms[fields][1]'), // name
          "2": formData.get('wpforms[fields][2]'), // email
          "3": formData.get('wpforms[fields][3]'), // phone
          "4": formData.get('wpforms[fields][4]'), // service
          "5": formData.get('wpforms[fields][5]'), // message
          source: 'wordpress_and_webhook'
        };

        const webhookResponse = await fetch('/api/webhook-contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookData)
        });
      } catch (webhookError) {
        // Webhook backup is optional - will be retried in fallback if WordPress fails
      }

      // Send to Portal webhook (new) - silently fail if CORS blocks it
      try {
        const portalData = {
          name: formData.get('wpforms[fields][1]'), 
          email: formData.get('wpforms[fields][2]'), 
          phone: formData.get('wpforms[fields][3]'), 
          service: formData.get('wpforms[fields][4]'), 
          message: formData.get('wpforms[fields][5]'),
        };

        const portalResponse = await fetch('https://portal.acceleratewebsites.com/api/webhooks/wpforms/partners-org-001', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(portalData),
          mode: 'cors',
          credentials: 'omit'
        });

        // Silently log portal success/failure - don't show to user
        if (portalResponse.ok) {
          console.log('✓ Portal webhook succeeded');
        }
      } catch (portalError) {
        // Portal webhook is optional - CORS may block it, that's okay
        // The local webhook is the primary submission method
      }

      setFormStatus('success');
      setFormMessage('Thank you! Your message has been sent. We\'ll get back to you within 2 hours during business hours.');
      form.reset();

    } catch (error) {
      // WordPress submission with no-cors mode - try webhook fallback
      
      // Fallback: Try to submit via our webhook
      try {
        console.log('Attempting webhook fallback...');
        const webhookData = {
          "1": formData.get('wpforms[fields][1]'), // name
          "2": formData.get('wpforms[fields][2]'), // email
          "3": formData.get('wpforms[fields][3]'), // phone
          "4": formData.get('wpforms[fields][4]'), // service
          "5": formData.get('wpforms[fields][5]'), // message
          source: 'webhook_fallback'
        };

        console.log('Webhook data:', webhookData);

        const webhookResponse = await fetch('/api/webhook-contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookData)
        });

        if (webhookResponse.ok) {
          console.log('Webhook submission successful');
          
          // Send to Portal webhook (fallback) - silently fail if CORS blocks it
          try {
            const portalData = {
              name: formData.get('wpforms[fields][1]'), 
              email: formData.get('wpforms[fields][2]'), 
              phone: formData.get('wpforms[fields][3]'), 
              service: formData.get('wpforms[fields][4]'), 
              message: formData.get('wpforms[fields][5]'),
            };

            const portalResponse = await fetch('https://portal.acceleratewebsites.com/api/webhooks/wpforms/partners-org-001', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(portalData),
              mode: 'cors',
              credentials: 'omit'
            });

            if (portalResponse.ok) {
              console.log('✓ Portal webhook succeeded (fallback)');
            }
          } catch (portalError) {
            // Portal webhook is optional - CORS may block it, that's okay
          }
          
          setFormStatus('success');
          setFormMessage('Thank you! Your message has been received. We\'ll get back to you within 2 hours during business hours.');
          form.reset();
        } else {
          const errorData = await webhookResponse.text();
          console.error('Webhook submission failed:', errorData);
          throw new Error('Webhook submission failed: ' + errorData);
        }
      } catch (webhookError) {
        console.error('Webhook submission error:', webhookError);
        setFormStatus('error');
        setFormMessage('There was an error sending your message. Please try calling us directly at ' + phoneNumber + ' or try again later.');
      }
    }
  };

  return (
    <>
      <Head>
        <title>Contact Local Plumber Geneva, IL | Kane County Plumbing Services</title>
        <meta name="description" content="Contact the best plumber near me in Geneva, St Charles & Kane County, IL. Emergency plumber available. Call (630) 715-8829 for fast plumbing help. Free quote." />
        <meta name="keywords" content="plumber near me, contact plumber Kane County, emergency plumber Geneva IL, plumbing quote Geneva, how much does a plumber cost Kane County, affordable plumber Geneva, fastest plumber Kane County" />
        <link rel="canonical" href="https://www.partnersinplumbing.com/contact" />
      </Head>

      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Your Local Plumber in Geneva, IL - Serving Kane County</h1>
            <p className="text-xl md:text-2xl mb-8 text-slate-100">Get professional plumbing services from the best plumber near me in Kane County - Emergency plumber available</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${phoneNumber}`} className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors">
                Call {phoneNumber}
              </a>
              <a href={`mailto:${email}`} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-slate-900 transition-colors">
                Email Us
              </a>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-8 bg-gray-100 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mb-2">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <span className="text-sm font-semibold text-gray-800">Licensed & Insured</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mb-2">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <span className="text-sm font-semibold text-gray-800">Licensed & Experienced</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-2">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <span className="text-sm font-semibold text-gray-800">Emergency Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 2 hours during business hours. For emergencies, please call us directly at {phoneNumber}.</p>
                
                {/* Form Status Messages */}
                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex">
                      <div className="text-green-600 mr-3">✓</div>
                      <p className="text-green-800">{formMessage}</p>
                    </div>
                  </div>
                )}
                
                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex">
                      <div className="text-red-600 mr-3">⚠</div>
                      <p className="text-red-800">{formMessage}</p>
                    </div>
                  </div>
                )}

                {/* Enhanced WPForms Integration */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <form 
                    method="POST" 
                    action={`${wpUrl}/wp-admin/admin-ajax.php`}
                    className="space-y-6"
                    onSubmit={handleSubmit}
                  >
                    <input type="hidden" name="action" value="wpforms_submit" />
                    <input type="hidden" name="wpforms[id]" value="48" />
                    <input type="hidden" name="wpforms[author]" value="1" />
                    <input type="hidden" name="wpforms[post_id]" value="0" />
                    <input type="hidden" name="wpforms[submit]" value="wpforms-submit" />
                    <input type="hidden" name="wpforms[token]" value="" />
                    
                    {/* Name Field */}
                    <div>
                      <label htmlFor="wpforms-48-field_1" className="block text-sm font-semibold text-gray-900 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="wpforms-48-field_1"
                        name="wpforms[fields][1]"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors disabled:bg-gray-100"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label htmlFor="wpforms-48-field_2" className="block text-sm font-semibold text-gray-900 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="wpforms-48-field_2"
                        name="wpforms[fields][2]"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors disabled:bg-gray-100"
                        placeholder="Enter your email address"
                      />
                    </div>

                    {/* Phone Field */}
                    <div>
                      <label htmlFor="wpforms-48-field_3" className="block text-sm font-semibold text-gray-900 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="wpforms-48-field_3"
                        name="wpforms[fields][3]"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors disabled:bg-gray-100"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    {/* Service Type Field */}
                    <div>
                      <label htmlFor="wpforms-48-field_4" className="block text-sm font-semibold text-gray-900 mb-2">
                        Service Needed
                      </label>
                      <select
                        id="wpforms-48-field_4"
                        name="wpforms[fields][4]"
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors disabled:bg-gray-100"
                      >
                        <option value="">Select a service</option>
                        <option value="Broken Pipe Repair">Broken Pipe Repair</option>
                        <option value="Leaky Pipe, Sink & Toilet Repair">Leaky Pipe, Sink & Toilet Repair</option>
                        <option value="Water Heater Replacement">Water Heater Replacement</option>
                        <option value="Reverse Osmosis Installation">Reverse Osmosis Installation</option>
                        <option value="Toilet Installation & Replacement">Toilet Installation & Replacement</option>
                        <option value="Emergency Water Cleanup">Emergency Water Cleanup</option>
                        <option value="Emergency Plumbing Service">Emergency Plumbing Service</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Message Field */}
                    <div>
                      <label htmlFor="wpforms-48-field_5" className="block text-sm font-semibold text-gray-900 mb-2">
                        Describe Your Plumbing Issue *
                      </label>
                      <textarea
                        id="wpforms-48-field_5"
                        name="wpforms[fields][5]"
                        rows={4}
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors resize-vertical disabled:bg-gray-100"
                        placeholder="Please describe your plumbing issue in detail..."
                      ></textarea>
                    </div>

                    {/* Anti-spam honeypot field */}
                    <div style={{display: 'none'} as React.CSSProperties}>
                      <input type="text" name="wpforms[hp]" value="" tabIndex={-1} autoComplete="off" />
                    </div>

                    {/* Additional security fields */}
                    <input type="hidden" name="wpforms[started]" value={Date.now().toString()} />
                    <input type="hidden" name="wpforms[completed]" value="" />

                    {/* Submit Button */}
                    <div>
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full bg-slate-800 text-white px-6 py-4 rounded-lg font-bold text-lg hover:bg-slate-900 transition-all duration-200 shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                </div>
                
                {/* Alternative: Call directly */}
                <div className="mt-6 text-center p-6 bg-slate-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Prefer to Call?</h4>
                  <p className="text-gray-600 mb-4">Speak directly with our plumbing experts</p>
                  <a href={`tel:${phoneNumber}`} className="text-2xl font-bold text-slate-800 hover:text-slate-900">
                    {phoneNumber}
                  </a>
                </div>
              </div>

              {/* Business Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Business Information</h2>
                
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Hours</h3>
                    <div className="space-y-2 text-gray-600">
                      <div className="flex justify-between">
                        <span>Monday - Friday:</span>
                        <span className="font-medium">8:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Saturday:</span>
                        <span className="font-medium">9:00 AM - 4:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sunday:</span>
                        <span className="font-medium">Emergency Only</span>
                      </div>
                      <div className="flex justify-between font-semibold text-red-600">
                        <span>Emergency Service:</span>
                        <span>Available</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mr-3">
                          <div className="w-5 h-5 bg-white rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Phone</p>
                          <a href={`tel:${phoneNumber}`} className="text-slate-800 hover:text-slate-900 font-bold">
                            {phoneNumber}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mr-3">
                          <div className="w-5 h-5 bg-white rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Email</p>
                          <a href={`mailto:${email}`} className="text-slate-800 hover:text-slate-900">
                            {email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mr-3">
                          <div className="w-5 h-5 bg-white rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Address</p>
                          <p className="text-gray-600">{address}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Why Choose Us?</h3>
                    <ul className="space-y-3 text-slate-800">
                      <li className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span>Licensed & Insured Plumbing Contractor</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span>Emergency Service Available</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span>Upfront Pricing - No Hidden Fees</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span>100% Satisfaction Guarantee</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span>Same-Day Service Available</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>Kane County</div>
                      <div>Geneva</div>
                      <div>Saint Charles</div>
                      <div>South Elgin</div>
                    </div>
                    <p className="text-sm text-gray-500 mt-3">
                      Don't see your area? <a href={`tel:${phoneNumber}`} className="text-slate-800 hover:text-slate-900 font-medium">Call us</a> - we may still be able to help!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Banner */}
        <section className="py-8 bg-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Plumbing Emergency?</h2>
            <p className="text-lg mb-4">
              Don't wait! Burst pipes, major leaks, and sewer backups need immediate attention.
            </p>
            <a href={`tel:${phoneNumber}`} className="bg-white text-red-600 px-4 sm:px-8 py-3 rounded-lg font-bold text-base sm:text-lg hover:bg-gray-100 transition-colors inline-block">
              Call Emergency Line: {phoneNumber}
            </a>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What to Expect When You Call</h2>
              <p className="text-xl text-gray-600">Professional service from start to finish</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <span className="text-slate-800 font-bold text-sm">1</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                <p className="text-gray-600">We'll discuss your plumbing issue and schedule a convenient time for service.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <span className="text-slate-800 font-bold text-sm">2</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Diagnosis</h3>
                <p className="text-gray-600">Our licensed technician will assess the problem and explain all your options.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <span className="text-slate-800 font-bold text-sm">3</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Upfront Pricing</h3>
                <p className="text-gray-600">You'll receive a clear, written estimate before any work begins - no surprises.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <span className="text-slate-800 font-bold text-sm">4</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality Repair</h3>
                <p className="text-gray-600">We'll complete the work efficiently and clean up after ourselves.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Find Us</h2>
              <p className="text-xl text-gray-600">Located in the heart of the Kane County community we serve</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-w-16 aspect-h-9">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/view?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&center=41.9406,-88.5300&zoom=10&maptype=roadmap`}
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Partners Plumbing Co. Location"
                  className="w-full h-96 sm:h-80 md:h-96"
                ></iframe>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Service Area</h3>
                    <address className="text-slate-800 font-bold not-italic">
                      Kane County, IL 60177
                    </address>
                    <p className="text-gray-600 mt-2">Serving Kane County and surrounding areas with professional plumbing services</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Directions</h3>
                    <p className="text-gray-600 mb-4">
                      Located in central Kane County for quick access to all surrounding communities.
                    </p>
                    <a 
                      href="https://www.google.com/maps/dir/?api=1&destination=1132+N+Lancaster+Circle,+Kane+County,+IL+60177"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-slate-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-900 transition-colors"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services you can trust. Licensed, insured, and reliable serving Kane County.</p>
                <a href={`tel:${phoneNumber}`} className="text-yellow-400 font-bold text-lg hover:text-yellow-300 block mb-2">
                  {phoneNumber}
                </a>
                <a href={`mailto:${email}`} className="text-gray-300 hover:text-white block mb-2">
                  {email}
                </a>
                <p className="text-gray-300 text-sm">
                  {address}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services/reverse-osmosis" className="hover:text-white">Reverse Osmosis</Link></li>
                  <li><Link href="/services/toilet-installation" className="hover:text-white">Toilet Installation</Link></li>
                  <li><Link href="/services/emergency-plumbing-service" className="hover:text-white">Emergency Plumbing</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                  <li><Link href="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link href="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved. | Licensed & Insured | Emergency Service Available</p>
              <p className="mt-2 text-sm">Licensed Plumbing Contractor | Serving Kane County</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
} 